/**
 * 开户 / 加开 BFF 入参出参埋点（与 App account_open_api_* 对齐）。
 * 由 http-interceptor 调用；勿与全站 api_request / api_response 混用。
 */

import { location } from 'cdui-js'
import { track } from '../track'

export const ACCOUNT_OPEN_API_EVENTS = {
  account_open_api_request: 'account_open_api_request',
  account_open_api_response: 'account_open_api_response'
} as const

export type AccountOpenBizModule = 'open_account' | 'add_account'

export type AccountOpenApiSource =
  | 'h5_open_account'
  | 'h5_add_account'
  | 'h5_edit_refused'
  | 'open_account_gate'
  | 'add_account_gate'
  | 'theme_bridge'
  | 'theme_bridge_cache'

/** H5 应对齐上报的业务 path（不含 host / 不含 /api/v1） */
export const ACCOUNT_OPEN_API_PATHS = new Set<string>([
  // 申请读写 / 提交
  '/user/account/apply/getByUserId',
  '/user/account/apply/getListByUserId',
  '/user/account/apply/getById',
  '/user/account/apply/save',
  '/user/account/apply/finishedSecuritiesAccountTypes',
  '/user/account/base/save',
  '/user/account/base/getByUserId',
  '/user/account/career/save',
  '/user/account/career/getByUserId',
  '/user/account/apply/submit',
  '/user/account/apply/signPdf',
  '/user/account/tax/W-8BEN',
  '/user/account/account/create',
  '/user/account/account/getByUserId',
  '/user/account/base/verifyOpenProfileFingerprint',
  '/user/account/bank/bind',
  // 风测
  '/user/riskAssessment/questionnaire',
  '/user/riskAssessment/submit',
  '/user/riskAssessment/latest',
  // 身份核验 / OCR / 证书
  '/idAuth/getIdCardOcrResult',
  '/idAuth/getBankCardOcrResult',
  '/idAuth/initFaceVerify',
  '/idAuth/livenessVerify',
  '/idAuth/livenessVerifyByCertifyId',
  '/idAuth/fourFactorVerify',
  '/idAuth/certificateApply',
  '/idAuth/filingRecordCompose',
  '/idAuth/signFieldFileAcquire',
  '/idAuth/themeAcquire',
  '/idAuth/verifyCertNoTwoElement',
  '/idAuth/findCertDown',
  '/idAuth/composeSignatureOnAttachment',
  // 上传
  '/file/upload',
  '/file/upload/private',
  '/file/uploadBase64'
])

const UPLOAD_API_PATHS = new Set([
  '/file/upload',
  '/file/upload/private',
  '/file/uploadBase64',
  '/idAuth/livenessVerify'
])

const OPEN_ACCOUNT_PATHS = [
  '/open-progress',
  '/account-result',
  '/w8ben',
  '/w8ben-result',
  '/open-account-docs'
]

const ADD_ACCOUNT_PATHS = ['/add-account', '/add-virtual-applyinfo']

const SENSITIVE_KEY_RE =
  /(password|passwd|pwd|token|refresh|secret|otp|verify_code|verifycode|id_card|idcard|bank_card|bankcard|card_no|cardno|authorization)/i

const BINARYISH_KEY_RE = /(video|file|signimg|signfile|sign_img|base64|p10|certp10|image)/i

const MAX_FIELD_STRING = 500
const MAX_JSON_CHARS = 4000

export type AccountOpenApiCtx = {
  biz_module: AccountOpenBizModule
  api_path: string
  http_method: string
  request_id: string
  source: AccountOpenApiSource
  start_ts: number
  /** 已打 request，避免重复 */
  requestTracked: boolean
}

export function normalizeAccountOpenApiPath(url: string): string {
  const raw = String(url || '').trim()
  if (!raw) return ''
  let pathname = raw
  try {
    if (/^[a-z]+:/i.test(raw)) {
      pathname = new URL(raw).pathname || raw.split('?')[0]
    } else {
      pathname = raw.split('?')[0]
    }
  } catch {
    pathname = raw.split('?')[0]
  }
  pathname = pathname.replace(/\/+$/, '') || '/'
  if (pathname.startsWith('/api/v1/')) {
    pathname = pathname.slice('/api/v1'.length) || '/'
  } else if (pathname === '/api/v1') {
    pathname = '/'
  }
  if (!pathname.startsWith('/')) pathname = `/${pathname}`
  return pathname
}

export function resolveAccountOpenBizContext(): {
  biz_module: AccountOpenBizModule
  source: AccountOpenApiSource
} | null {
  const path = String(location.path || '').split('?')[0]
  if (ADD_ACCOUNT_PATHS.some((p) => path === p)) {
    return { biz_module: 'add_account', source: 'h5_add_account' }
  }
  if (OPEN_ACCOUNT_PATHS.some((p) => path === p)) {
    const mode = String(location.query?.mode ?? '')
    if (mode === 'editRefused' || mode === 'backReopen') {
      return { biz_module: 'open_account', source: 'h5_edit_refused' }
    }
    return { biz_module: 'open_account', source: 'h5_open_account' }
  }
  return null
}

export function shouldTrackAccountOpenApi(apiPath: string): boolean {
  if (!ACCOUNT_OPEN_API_PATHS.has(apiPath)) return false
  return resolveAccountOpenBizContext() != null
}

function truncateString(value: string, max: number): string {
  if (value.length <= max) return value
  return `${value.slice(0, max)}…`
}

function redactPrimitive(key: string, value: unknown): unknown {
  if (SENSITIVE_KEY_RE.test(key)) return '[Redacted]'
  if (typeof value === 'string') {
    if (BINARYISH_KEY_RE.test(key) && value.length > 64) {
      return `[omitted:${value.length}]`
    }
    return truncateString(value, MAX_FIELD_STRING)
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (value == null) return value
  return value
}

function redactDeep(input: unknown, keyHint = ''): unknown {
  if (input == null) return input
  if (typeof input !== 'object') return redactPrimitive(keyHint, input)
  if (Array.isArray(input)) {
    return input.map((item, i) => redactDeep(item, `${keyHint}[${i}]`))
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (SENSITIVE_KEY_RE.test(k)) {
      out[k] = '[Redacted]'
      continue
    }
    if (v instanceof File || v instanceof Blob) {
      out[k] = {
        fileName: v instanceof File ? v.name : 'blob',
        size: v.size,
        type: v.type
      }
      continue
    }
    out[k] = redactDeep(v, k)
  }
  return out
}

export function toRedactedJsonString(data: unknown): string | undefined {
  if (data === undefined) return undefined
  try {
    const redacted = redactDeep(data)
    const text = JSON.stringify(redacted)
    if (text == null) return undefined
    return truncateString(text, MAX_JSON_CHARS)
  } catch {
    return undefined
  }
}

function parseQueryParams(url: string): Record<string, string> {
  const q = url.indexOf('?')
  if (q < 0) return {}
  const out: Record<string, string> = {}
  try {
    const search = new URLSearchParams(url.slice(q + 1))
    search.forEach((v, k) => {
      out[k] = v
    })
  } catch {
    /* ignore */
  }
  return out
}

function formDataToMeta(form: FormData, apiPath: string): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  let fileMeta: { fileName?: string; size?: number; useFor?: string } | null = null

  form.forEach((value, key) => {
    const trimmedKey = key.trim()
    // FormDataEntryValue = string | File
    if (typeof value !== 'string') {
      const meta = {
        fileName: value.name || 'blob',
        size: value.size,
        type: value.type
      }
      out[trimmedKey] = meta
      if (!fileMeta) {
        fileMeta = { fileName: meta.fileName, size: meta.size }
      }
      return
    }
    const text = value
    if (trimmedKey === 'useFor' && fileMeta) fileMeta.useFor = text
    if (UPLOAD_API_PATHS.has(apiPath) && BINARYISH_KEY_RE.test(trimmedKey)) {
      out[trimmedKey] = `[omitted:${text.length}]`
      return
    }
    out[trimmedKey] = redactPrimitive(trimmedKey, text)
  })

  if (UPLOAD_API_PATHS.has(apiPath) && fileMeta) {
    const useFor = typeof out.useFor === 'string' ? out.useFor : fileMeta.useFor
    return {
      fileName: fileMeta.fileName,
      size: fileMeta.size,
      ...(useFor ? { useFor } : {}),
      ...(out.busId != null ? { busId: out.busId } : {}),
      ...(out.isOCR != null ? { isOCR: out.isOCR } : {})
    }
  }
  return out
}

function parseBody(body: BodyInit | null | undefined, apiPath: string): Record<string, unknown> | unknown {
  if (body == null || body === '') return {}
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return formDataToMeta(body, apiPath)
  }
  if (typeof body === 'string') {
    const trimmed = body.trim()
    if (!trimmed) return {}
    try {
      return JSON.parse(trimmed) as unknown
    } catch {
      return { raw: truncateString(trimmed, MAX_FIELD_STRING) }
    }
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    const out: Record<string, string> = {}
    body.forEach((v, k) => {
      out[k] = v
    })
    return out
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return { size: body.size, type: body.type }
  }
  return {}
}

export function buildAccountOpenRequestParams(
  url: string,
  body: BodyInit | null | undefined,
  apiPath: string
): string | undefined {
  const query = parseQueryParams(url)
  const bodyObj = parseBody(body, apiPath)
  const merged: Record<string, unknown> = { ...query }
  if (bodyObj && typeof bodyObj === 'object' && !Array.isArray(bodyObj)) {
    Object.assign(merged, bodyObj as Record<string, unknown>)
  } else if (bodyObj !== undefined && !(typeof bodyObj === 'object' && Object.keys(bodyObj as object).length === 0)) {
    merged.body = bodyObj
  }
  if (Object.keys(merged).length === 0) return undefined
  return toRedactedJsonString(merged)
}

export function trackAccountOpenApiRequest(props: {
  biz_module: AccountOpenBizModule
  api_path: string
  http_method: string
  request_id: string
  request_params?: string
  source?: AccountOpenApiSource
}): void {
  const payload: Record<string, unknown> = {
    biz_module: props.biz_module,
    api_path: props.api_path,
    http_method: props.http_method,
    request_id: props.request_id
  }
  if (props.request_params) payload.request_params = props.request_params
  if (props.source) payload.source = props.source
  track(ACCOUNT_OPEN_API_EVENTS.account_open_api_request, payload)
}

export function trackAccountOpenApiResponse(props: {
  biz_module: AccountOpenBizModule
  api_path: string
  http_method: string
  request_id: string
  is_success: boolean
  duration_ms: number
  response_body?: string
  http_code?: number
  biz_code?: number
  trace_id?: string
  is_network_error?: boolean
  error_message?: string
  source?: AccountOpenApiSource
}): void {
  const payload: Record<string, unknown> = {
    biz_module: props.biz_module,
    api_path: props.api_path,
    http_method: props.http_method,
    request_id: props.request_id,
    is_success: props.is_success,
    duration_ms: Math.max(0, Math.round(props.duration_ms))
  }
  if (props.response_body) payload.response_body = props.response_body
  if (typeof props.http_code === 'number' && Number.isFinite(props.http_code)) {
    payload.http_code = props.http_code
  }
  if (typeof props.biz_code === 'number' && Number.isFinite(props.biz_code)) {
    payload.biz_code = props.biz_code
  }
  if (props.trace_id?.trim()) payload.trace_id = props.trace_id.trim()
  if (props.is_network_error === true) payload.is_network_error = true
  if (props.error_message?.trim()) {
    payload.error_message = truncateString(props.error_message.trim(), 200)
  }
  if (props.source) payload.source = props.source
  track(ACCOUNT_OPEN_API_EVENTS.account_open_api_response, payload)
}

/** 请求侧：命中白名单 + 开户/加开路由时挂 ctx 并打 request */
export function beginAccountOpenApiTracking(
  url: string,
  options: RequestInit & { __accountOpenApi?: AccountOpenApiCtx },
  ids: { request_id: string; trace_id?: string }
): AccountOpenApiCtx | null {
  const api_path = normalizeAccountOpenApiPath(url)
  if (!shouldTrackAccountOpenApi(api_path)) return null
  const biz = resolveAccountOpenBizContext()
  if (!biz) return null

  const http_method = String(options.method || 'GET').toUpperCase()
  const ctx: AccountOpenApiCtx = {
    biz_module: biz.biz_module,
    api_path,
    http_method,
    request_id: ids.request_id,
    source: biz.source,
    start_ts: Date.now(),
    requestTracked: true
  }
  options.__accountOpenApi = ctx

  trackAccountOpenApiRequest({
    biz_module: ctx.biz_module,
    api_path: ctx.api_path,
    http_method: ctx.http_method,
    request_id: ctx.request_id,
    request_params: buildAccountOpenRequestParams(url, options.body, api_path),
    source: ctx.source
  })
  return ctx
}

export function finishAccountOpenApiTracking(
  options: (RequestInit & { __accountOpenApi?: AccountOpenApiCtx }) | undefined,
  result: {
    is_success: boolean
    http_code?: number
    biz_code?: number
    response_body?: unknown
    trace_id?: string
    is_network_error?: boolean
    error_message?: string
  }
): void {
  const ctx = options?.__accountOpenApi
  if (!ctx?.requestTracked) return
  // 防止 response + network 双报
  options!.__accountOpenApi = { ...ctx, requestTracked: false }

  trackAccountOpenApiResponse({
    biz_module: ctx.biz_module,
    api_path: ctx.api_path,
    http_method: ctx.http_method,
    request_id: ctx.request_id,
    is_success: result.is_success,
    duration_ms: Date.now() - ctx.start_ts,
    response_body: toRedactedJsonString(result.response_body),
    http_code: result.http_code,
    biz_code: result.biz_code,
    trace_id: result.trace_id,
    is_network_error: result.is_network_error,
    error_message: result.error_message,
    source: ctx.source
  })
}
